import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { EMPLOYER_FIXED_PROPERTIES } from '@ptg-employer/constance/employer.const';
import { EmployerCheckDateOfBirthRequest, EmployerCheckDisableRequest } from '@ptg-employer/models/employer.model';
import {
  ENTITY_MAPPING,
  FixedPropertyKey,
  LIMIT_PHONE_NUMBER,
  LIST_INPUT_ADDRESS,
  LIST_INPUT_PERSON_NAME,
  LIST_INPUT_STATUS,
  PERSON_BASE_PROPERTIES,
  StatusCourtOrder,
  UnitedStates,
} from '@ptg-entity-management/constants';

import { COURT_ORDER_TYPE_CODE, FixedPropertyId } from '@ptg-entity-management/constants/index';

import { EntityDataService, EntityPropertyService } from '@ptg-entity-management/services';
import {
  EntityInitiationPropertyValue,
  InitiationProperty,
  PayeeOption,
  PayeeRequest,
  ValidateReportedDeceasedResponse,
} from '@ptg-entity-management/services/models';
import {
  clearPayeePropertiesStateAction,
  getPayeePropertiesAction,
} from '@ptg-entity-management/store/actions/entity-property.action';
import { getPayeePropertiesSelector } from '@ptg-entity-management/store/selectors/entity-property.selector';
import {
  CourtOrderType,
  EntityPropertyType,
  RelationshipOptions,
  StaticPropertyType,
} from '@ptg-entity-management/types/enums';
import { isEINProperty } from '@ptg-member/helper';
import { AddressHistoryAction, clearSetMemberEventStateAction } from '@ptg-member/store/actions';
import { BaseComponent } from '@ptg-shared/components';
import {
  BUTTON_LABEL_CLOSE,
  ENTITY_BENEFICIARIES_LIST_GUID,
  ENTITY_DEPENDENTS_LIST_GUID,
  ENTITY_ORGANIZATION_GUID,
  ENTITY_PARTICIPANT_GUID,
  ENTITY_PERSON_GUID,
  ENTITY_POWER_OF_ATTORNEY_LIST_GUID,
} from '@ptg-shared/constance';

import { ConfirmType } from '@ptg-shared/constance/confirm-type.const';
import { ConfirmPopupComponent } from '@ptg-shared/controls/confirm-popup/confirm-popup.component';
import { MY_DATE } from '@ptg-shared/controls/datepicker/datepicker.component';
import { Option } from '@ptg-shared/controls/select/select.component';
import { LayoutService } from '@ptg-shared/services/layout.service';
import { FundType, LookupTableType, ValidationOperator, ValidationType } from '@ptg-shared/types/enums';
import {
  DateValidationValue,
  FieldData,
  FormControlDetail,
  OptionValue,
  PropValidation,
} from '@ptg-shared/types/models';
import { AbstractControlStatus } from '@ptg-shared/types/models/common.model';
import { deepClone, toCamelCase } from '@ptg-shared/utils/common.util';
import {
  getDateString,
  getValueWithoutFormat,
  isEmpty,
  isString,
  setFractionalLength,
  stringToBoolean,
} from '@ptg-shared/utils/string.util';
import { checkApiValidator } from '@ptg-shared/validators/checkApi.validator';
import { checkUnique } from '@ptg-shared/validators/checkUnique.validator';
import { forkJoin, Observable, of, Subject, timer } from 'rxjs';
import { debounceTime, filter, finalize, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import * as fromMember from '../../../member/store/reducers';
import { EntityDataState } from '@ptg-entity-management/store/reducers';
import { checkIfRecordExistedSelector } from '@ptg-entity-management/store/selectors';

@Component({
  selector: 'ptg-upsert-entity-data',
  templateUrl: './upsert-entity-data.component.html',
  styleUrls: ['./upsert-entity-data.component.scss'],
})
export class UpsertEntityDataComponent extends BaseComponent implements AfterViewInit {
  readonly EntityPropertyType = EntityPropertyType;
  readonly StaticPropertyType = StaticPropertyType;
  readonly MY_DATE = MY_DATE;
  readonly FixedPropertyKey = FixedPropertyKey;
  readonly UnitedStates = UnitedStates;

  @Input() properties!: InitiationProperty[];
  @Input() listRecordId!: string[];
  @Input() entityId!: string;
  @Input() data: any;
  @Input() cardInfor!: {
    id: string;
    isSummaryView: boolean;
    isList: boolean;
  };
  @Input() isSubmitting: boolean = false;
  @Input() isEdit: boolean = false;
  @Input() isEditCard?: boolean = false;
  @Input() targetId: string = '';
  @Input() recordId: string = '';
  @Input() screenId?: string;
  @Input() isEffectiveData: boolean = false;
  @Input() entityComponentId!: string;
  @Output() editSectionEvent = new EventEmitter();
  @Output() addNewEvent = new EventEmitter();
  @Output() cancelEvent = new EventEmitter();

  formGroup: FormGroup = new FormGroup({});
  erFormGroup: any = {};
  erFormRadioButton: any = {};
  controls: FormControlDetail[] = [];
  erControls: any = {};
  propertyReferences!: InitiationProperty[];
  listAllStatus: any[] = [];
  listStatusEvent: Option[] = [];
  selectedRecordId: any = {};

  isBVFFFund = this.layoutService.fundType === FundType.BVFF;

  currentMemberDetailUnmask: any;
  datePropValidation: PropValidation[] = [];
  dateValidationValue: DateValidationValue[] = [];
  currentInactivationDate = '';
  validatorPrevious: any = {};
  formSubmit$ = new Subject<boolean>();
  payeeId: string = '';
  spousePerson?: any;
  entityReal: string = '';
  currentRelationshipValue?: number;
  isSelectedSpouse!: boolean;
  relationshipLookup: any;
  isAddNew: boolean = false;
  listMemberParticipants = [ENTITY_MAPPING.Member, ENTITY_PERSON_GUID, ENTITY_PARTICIPANT_GUID];
  isRecordExisted: boolean = false;
  entityReferenceRequiredMessage: string = '';

  constructor(
    private dialog: MatDialog,
    private entityPropertyService: EntityPropertyService,
    private entityDataService: EntityDataService,
    private entityDataStore: Store<EntityDataState>,
    private memberStore: Store<fromMember.MemberState>,
    private readonly layoutService: LayoutService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this._loadPropertiesToForm(this.properties, this.data);
    if (this.dateOfDeathControl && this.lineOfDutyDeathControl) {
      this._onChangeDateOfDeathValue();
    }
    if (this.newHireExamControl && this.newHireExamDateControl) {
      this._onChangeNewHireExamValue();
    }
    if (this.inactivationDateControl) {
      this._onChangeInactivationDateValue();
    }
    this.formatSSNAndDOBMember();
    this.onSubmit();
    this.getPayeeProperties();
    this._checkIfRecordExistedSelector();
  }

  ngAfterViewInit() {
    if (this.data) {
      this._setValue(this.data);
      this.initFormCourtOrder();
    }
  }

  formatSSNAndDOBMember(){
      const isNotPerson = ENTITY_PERSON_GUID !== this.entityId;
      const idx = this.properties.findIndex(el => el.type === EntityPropertyType['Entity Reference']);
      if(idx === -1){
        this.properties = this.properties.map(elm => {
          const SSNAndDOBType = [PERSON_BASE_PROPERTIES.SSN, PERSON_BASE_PROPERTIES.DateOfBirth].includes(elm.entityPropertyId.toUpperCase());
          return {
            ...elm,
            configs: {
              ...elm.configs,
              required: SSNAndDOBType && isNotPerson ? 'true' : elm.configs.required
            }
          }
        });
      };
      this.controls = this.controls.map(el => {
        const SSNAndDOBType = [PERSON_BASE_PROPERTIES.SSN, PERSON_BASE_PROPERTIES.DateOfBirth].includes(el.name.toUpperCase());
        return {
          ...el,
          isRequired: SSNAndDOBType && isNotPerson ? true : el.isRequired
        }
      });
  }

  initFormCourtOrder() {
    const checkCourtOrderType = this.controls?.find((item: FormControlDetail) => item?.configs?.fixedKey === FixedPropertyKey.CourtOrderType);
    if (checkCourtOrderType) {
      const selectedCourtOrder = checkCourtOrderType?.lstOption?.find((item: any) => item.value === checkCourtOrderType?.formControl.value);
      this.payeeId = this.payeeControl.value;
      const body = {
        memberId: this.targetId,
        courtOrderType:
          selectedCourtOrder.code === COURT_ORDER_TYPE_CODE.QDRO ? CourtOrderType.QDRO : CourtOrderType.Garnishment,
        screenId: this.screenId,
        courtOrderComponentId: this.entityComponentId,
        payeeId: this.payeeId,
        courtOrderId: this.recordId
      };

      this.memberStore.dispatch(getPayeePropertiesAction({
        body
      }));
      const checkRelationship = this.controls?.find(item => item.configs?.fixedKey === FixedPropertyKey.RelationShip);
      if (checkRelationship) {
        checkRelationship.isHidden = selectedCourtOrder.code !== COURT_ORDER_TYPE_CODE.QDRO;
        checkRelationship.isRequired = selectedCourtOrder.code === COURT_ORDER_TYPE_CODE.QDRO;
        checkRelationship?.formControl.setValue('');
      }
    }

    const checkStatusCourtOrder = this.controls.find((item: FormControlDetail)  => item?.configs?.fixedKey === FixedPropertyKey.StatusCourtOrder);
    if (checkStatusCourtOrder && checkStatusCourtOrder.formControl.value === StatusCourtOrder.Approved) {
      checkStatusCourtOrder.lstOption = checkStatusCourtOrder.lstOption.filter(
        (item) => item.value !== StatusCourtOrder.Pending,
      );
      this.controls.forEach((item) => {
        if (
          item.configs?.fixedKey === FixedPropertyKey.StatusCourtOrder ||
          item.configs?.fixedKey === FixedPropertyKey.RejectReason
        ) {
          item.disableRow = false;
        } else {
          item.disableRow = true;
        }
      });
    }
  }

  private _setValue(data: any) {
    let value: any;
    this.controls.forEach((element) => {
      if (element.type === EntityPropertyType.Aggregation || element.type === EntityPropertyType.Calculation) {
        element.isHidden = true;
      }
      if (element?.fixedPropertyKey) {
        value = data?.find((item: any) => item.fixedPropertyKey === element.fixedPropertyKey)?.value;
        element.formControl?.setValue(value);
      } else {
        value = data?.find((item: any) => item.entityPropertyId === element.name)?.value;
        if (value?.maskedValue || value?.maskedValue === null) {
          value = value.originalValue;
        }
        if (isEmpty(value) && element.type !== EntityPropertyType.Binary) {
          return;
        }
        if (
          element.type == EntityPropertyType.Address ||
          element.type == EntityPropertyType['Person Name'] ||
          element.type == EntityPropertyType.Status
        ) {
          // Find corresponding address
          const address = data.find((el: any) => el.type === EntityPropertyType.Address && el.entityPropertyId === element.name);
          element.lstChildFormControl?.forEach((item: any) => {
            if (item?.key === 'country') {
              if (value[item.key]) {
                item.formControl?.setValue(value[item.key]);
              }
            } else if (item?.key === 'state') {
              const selectedCountryCode = address?.options?.find((el: any) => el.id === address?.value?.country)?.description;
              if (selectedCountryCode && !['USA', 'CA', 'CAN'].includes(selectedCountryCode)) {
                item.type = 'text';
                item.required = false;
                item.isSetRequired = false;
                item.formControl?.removeValidators(Validators.required);
                item.formControl?.setValue(address?.options.find((el: any) => el.id === address?.value?.state)?.text);
              } else {
                item.formControl?.setValue(value[item.key] || null);
              }
            } else if (item?.key === 'zipCode') {
              const selectedCountryCode = address?.options?.find((el: any) => el.id === address?.value?.country)?.description;
              if (selectedCountryCode && !['USA', 'CA', 'CAN'].includes(selectedCountryCode)) {
                item.required = false;
                item.isSetRequired = false;
                setTimeout(() => {
                  item?.formControl?.clearValidators();
                  item?.formControl?.setErrors(null);
                  item?.formControl?.updateValueAndValidity();
                });
              } else if (selectedCountryCode && selectedCountryCode !== 'USA') {
                item.required = false;
                item.isSetRequired = false;
                setTimeout(() => {
                  item.formControl?.removeValidators(Validators.required);
                  item?.formControl?.setErrors(null);
                  item?.formControl?.updateValueAndValidity();
                });
              }
              item.formControl?.setValue(address?.value?.zipCode);
            } else {
              item.formControl?.setValue(
                item?.key === 'effectFrom' && value?.isClearEffectFrom ? null : value[item.key] || null,
              );
            }
          });
          if (element.type == EntityPropertyType.Address) {
            element.isActive = value?.id ? true : false;
            element.code = value?.code;
            element.data.addressId = value.id;
            this.changeValueInputAddress(element);
          }
        } else if (element.type == EntityPropertyType.Phone) {
          element.formControl?.setValue(this.formatPhoneNumberInput(value));
        } else if (element.type == EntityPropertyType.Binary) {
          element.formControl?.setValue(value === 'true' || value ? true : false);
        } else if (element.type === EntityPropertyType['Date Time']) {
          element.formControl?.setValue(getDateString(value));
        } else {
          if (element?.fractionalLength) {
            const fractionalLengthValue = setFractionalLength(value, element?.fractionalLength);
            element.formControl?.setValue((+value)?.toFixed(fractionalLengthValue));
          } else {
            element.formControl?.setValue(value);
          }
        }
      }
    });
    this.currentInactivationDate = this._findPropertyByFixedKey(data, EMPLOYER_FIXED_PROPERTIES.InactivationDate)
      ?.value;
  }

  formatPhoneNumberInput(formControlValue: string) {
    const cleaned = ('' + formControlValue).trim().replace(/[^0-9]/g, '');
    let match = cleaned.match(LIMIT_PHONE_NUMBER);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return formControlValue;
  }

  private _loadPropertiesToForm(properties: InitiationProperty[] = [], cardData?: any): void {
    this.controls = [...properties]
      .filter(
        (prop) =>
          prop.type !== EntityPropertyType['Entity Reference'] &&
          // Auto Generated Identifier Property should not be show in Add new Member screen,
          // but still show in Edit Member screen as a label with value
          !(
            prop.type === EntityPropertyType.Identifier &&
            stringToBoolean(prop?.configs?.autogenerated) === true &&
            this.isEditCard === false
          ),
      )
      .map((prop) => {
        const isIdentifierAutoGeneratedPropertyType =
          prop.type === EntityPropertyType.Identifier && stringToBoolean(prop?.configs?.autogenerated) === true;
        let control: any;
        if (
          prop.configs.fixedKey &&
          [FixedPropertyKey.Payee, FixedPropertyKey.StatusCourtOrder].includes(prop.configs.fixedKey)
        ) {
          prop.type = EntityPropertyType.Lookup;
        }
        if (isEINProperty(prop.entityPropertyId)) {
          (prop.type as any) = 'EIN';
        }
        if (cardData) {
          control = cardData?.find((item: any) => item.entityPropertyId === prop.entityPropertyId);
        }
        if (prop.type === EntityPropertyType.Address || prop.type === EntityPropertyType['Person Name']) {
          // Address type | Person name type
          let listInputAddressDefault = deepClone(LIST_INPUT_ADDRESS);
          if (!this.isEffectiveData || this.cardInfor?.isList || !this.listMemberParticipants.includes(this.entityId)) {
            listInputAddressDefault = deepClone(LIST_INPUT_ADDRESS).filter((item: any) => {
              return item?.key !== 'effectTo' && item?.key !== 'effectFrom';
            });
          }
          let defaultCountry = '';
          const childFields =
            prop.type === EntityPropertyType.Address
              ? deepClone(listInputAddressDefault)
              : deepClone(LIST_INPUT_PERSON_NAME);
          childFields.forEach((field: FieldData) => {
            field.formControl = new FormControl(null);
            field.lstOption = [];

            if (prop.options?.length) {
              if (field.type === 'select' && prop.configs?.prefixList === 'true') {
                field.lstOption = prop.options.map((x: any) => ({
                  displayValue: x.text,
                  value: x.id,
                }));
              } else if (field.key === 'state') {
                field.lstOption = prop.options
                  .filter((option: OptionValue) => option.type === LookupTableType.State)
                  .map((option: OptionValue) => ({
                    displayValue: option.text,
                    value: option.id,
                  }));
              } else if (field.key === 'country') {
                field.lstOption = prop.options
                  .filter((option: OptionValue) => option.type === LookupTableType.Country)
                  .map((option: OptionValue) => ({
                    displayValue: option.text,
                    value: option.id,
                    code: option.description,
                  }));
                defaultCountry = field.lstOption?.find(item => item.code === this.UnitedStates)?.value;
                field.formControl.setValue(defaultCountry ? defaultCountry : null);
              }
            }

            if (stringToBoolean(prop.configs?.readOnly)) {
              field.formControl.disable();
            }
          });
          if (prop.type === EntityPropertyType.Address && defaultCountry && prop.configs?.required === 'false') {
            childFields?.forEach((item: FieldData) => {
              if (!['effectTo', 'street2'].includes(item.key)) {
                item.isSetRequired = true;
                item?.formControl?.addValidators(Validators.required);
                item?.formControl?.updateValueAndValidity();
              }
            });
          }
          const fromElement = childFields?.find((el) => el.key === 'effectFrom');
          const toElement = childFields?.find((el) => el.key === 'effectTo');
          fromElement?.formControl?.addValidators(this._effectiveDate(fromElement, toElement));
          const controlDetail = {
            name: prop.entityPropertyId,
            recordId: control?.recordId,
            label: prop.label,
            type: prop.type,
            lstChildFormControl: childFields,
            isRequired: prop.configs?.required === 'true',
            data: prop,
          } as FormControlDetail;
          if (prop.type === EntityPropertyType.Address) {
            this.changeValueInputAddress(controlDetail);
          }
          return controlDetail;
        } else if (prop.type === EntityPropertyType.Status) {
          // Status type | Person name type
          const childFields = deepClone(LIST_INPUT_STATUS);
          childFields.forEach((field: FieldData) => {
            field.formControl = new FormControl(null);
            field.lstOption = [];

            if (prop.options?.length && field.key === 'status') {
              field.lstOption = prop.options
                .filter((item) => this.isEdit || item.active)
                ?.map((item) => {
                  return {
                    value: item.id,
                    displayValue: item.name,
                  };
                });
              this.listAllStatus = prop.options;
            }
          });

          return {
            name: prop.entityPropertyId,
            recordId: control?.recordId,
            label: prop.label,
            type: prop.type,
            lstChildFormControl: childFields,
            data: prop,
          } as FormControlDetail;
        } else {
          // One input type
          const formControl = new FormControl(null);
          const controlDetail: FormControlDetail = {
            name: prop.entityPropertyId,
            recordId: control?.recordId,
            label: prop.label,
            type: prop.type,
            lstOption: [],
            formControl: formControl,
            isRequired: prop.configs?.required === 'true',
            maxLength: 'none',
            ...(isIdentifierAutoGeneratedPropertyType && {
              isIdentifierAutoGeneratedPropertyType: true,
            }),
            fixedPropertyKey: prop.fixedPropertyKey,
            staticPropertyType: prop.staticPropertyType,
          };
          // validate by property config
          const isExistingMinVal =
            stringToBoolean(prop.configs?.inclusiveInRange) &&
            isString(prop.configs?.minInput) &&
            !isNaN(Number(prop.configs?.minInput));
          const isExistingMaxVal =
            stringToBoolean(prop.configs?.inclusiveInRange) &&
            isString(prop.configs?.maxInput) &&
            !isNaN(Number(prop.configs?.maxInput));
          const isExistingMaxLengthVal =
            stringToBoolean(prop.configs?.maximumLength) &&
            isString(prop.configs?.maxLengthInput) &&
            !isNaN(Number(prop.configs?.maxLengthInput));
          const isExistingFracLengthVal =
            stringToBoolean(prop.configs?.fractionalLength) &&
            isString(prop.configs?.fractionalLengthInput) &&
            !isNaN(Number(prop.configs?.fractionalLengthInput));

          if (isExistingMinVal) {
            controlDetail.min = prop.configs?.minInput.trim() ? prop.configs?.minInput.trim() : undefined;
          }
          if (isExistingMaxVal) {
            controlDetail.max = prop.configs?.maxInput.trim() ? prop.configs?.maxInput.trim() : undefined;
          }
          if (isExistingMaxLengthVal && !isExistingMinVal && !isExistingMaxVal) {
            controlDetail.maxLength = prop.configs?.maxLengthInput;
          }
          let propTransformedType = prop.type;
          if (isExistingFracLengthVal) {
            const fractionalLength = Number(prop.configs?.fractionalLengthInput);
            if (fractionalLength === 0) {
              propTransformedType = EntityPropertyType['Whole Number'];
            } else {
              const rgx = new RegExp(`^[+-]?[0-9]{1,999999}(?:\\.[0-9]{0,${fractionalLength}})?$`);
              formControl.addValidators(Validators.pattern(rgx));
              controlDetail.fractionalLength = fractionalLength.toString();
            }
          }

          switch (prop?.type) {
            case EntityPropertyType.Currency:
              formControl.setValue(null);
              break;
            case EntityPropertyType.Decimal:
              if (!isExistingFracLengthVal) {
                propTransformedType = EntityPropertyType['Whole Number'];
              }
              break;
            case EntityPropertyType.Percentage:
              formControl.addValidators(Validators.min(0));
              controlDetail.min = 0;

              formControl.addValidators(Validators.max(100));
              controlDetail.max = 100;

              const rgx = new RegExp(`^[+-]?[0-9]{1,999999}(?:\\.[0-9]{0,${2}})?$`);
              formControl.addValidators(Validators.pattern(rgx));
              controlDetail.fractionalLength = 2;
              break;
            case EntityPropertyType.Date:
              let minDate: Date | undefined = undefined;
              let maxDate: Date | undefined = undefined;

              const currentDate = new Date();
              currentDate.setHours(0, 0, 0, 0);

              if (prop.configs?.excludeFutureDates?.toLowerCase() == 'true') {
                currentDate.setHours(24);
                maxDate = currentDate;
              }

              if (prop.configs?.excludePastDates?.toLowerCase() == 'true') {
                currentDate.setHours(-24);
                minDate = currentDate;
              }

              if (prop.configs?.dateValidation?.toLowerCase() == 'true') {
                controlDetail.isValidation = true;
                const rules = JSON.parse(prop.configs.dateValidationExpressions);
                if (rules?.length > 0) {
                  rules.forEach((rule: any) => {
                    switch (rule.validationType) {
                      case ValidationType.Year:
                        const yearValidation = +rule.value;
                        if (yearValidation) {
                          const vaidationDate = new Date();
                          vaidationDate.setFullYear(vaidationDate.getFullYear() - yearValidation);
                          if (rule.validation === ValidationOperator.LessThan) {
                            let dateVal = new Date(vaidationDate);
                            dateVal.setHours(0);
                            if (minDate == undefined || dateVal > minDate) {
                              minDate = dateVal;
                            }
                          }

                          if (rule.validation === ValidationOperator.GreaterThan) {
                            let dateVal = new Date(vaidationDate);
                            dateVal.setHours(0);
                            if (maxDate == undefined || dateVal < maxDate) {
                              maxDate = dateVal;
                            }
                          }
                        }
                        break;
                      case ValidationType.SpecificDate:
                        const dateValidation = rule.value;
                        if (dateValidation) {
                          if (rule.validation === ValidationOperator.LessThan) {
                            const dateVal = new Date(dateValidation);
                            dateVal.setHours(0);
                            if (maxDate == undefined || dateVal < maxDate) {
                              maxDate = dateVal;
                            }
                          }

                          if (rule.validation === ValidationOperator.GreaterThan) {
                            const dateVal = new Date(dateValidation);
                            dateVal.setHours(0);
                            if (minDate == undefined || dateVal > minDate) {
                              minDate = dateVal;
                            }
                          }
                        }
                        break;
                      case ValidationType.Property:
                        this.datePropValidation.push({
                          property: prop.entityPropertyId,
                          validationProp: rule.value,
                          operator: rule.validation,
                        });
                    }
                  });
                }
              }

              this.dateValidationValue.push({
                property: prop.entityPropertyId,
                max: maxDate,
                min: minDate,
              });

              if (minDate) {
                this.setMinDate(controlDetail, minDate);
              }

              if (maxDate) {
                this.setMaxDate(controlDetail, maxDate);
              }

              // check control is inactivation date
              if (prop?.configs?.fixedKey && prop?.configs?.fixedKey === EMPLOYER_FIXED_PROPERTIES.InactivationDate) {
                maxDate = currentDate;
                controlDetail.max = new Date();
                controlDetail.maxMessage = 'Inactivation Date should not be greater than today.';
                controlDetail.formControl.addAsyncValidators(this._validateInactivationDate());
              }
              if (!prop?.configs?.fixedKey || prop?.configs?.fixedKey !== EMPLOYER_FIXED_PROPERTIES.InactivationDate) {
                this._setDateValidationMessage(controlDetail);
              }

              // Validate DateOfBirth only
              if (this.isBVFFFund && prop?.entityPropertyId?.toLocaleLowerCase() === PERSON_BASE_PROPERTIES.DateOfBirth.toLocaleLowerCase() && this.entityId === ENTITY_MAPPING.Member) {
                controlDetail.formControl.addAsyncValidators(this._validateDateOfBirth());
              }
              break;
            case EntityPropertyType.Binary:
              if (prop?.entityPropertyId?.toLocaleLowerCase() === PERSON_BASE_PROPERTIES.ReportedDeceased.toLocaleLowerCase()) {
                controlDetail.formControl.addAsyncValidators(this._validateReportedDeceased());
                controlDetail.customError = 'customError';
              }
              break;
            default:
              break;
          }

          // set list option for List type
          let lstOption: any[] = [];
          if (
            (prop.type === EntityPropertyType.Lookup || prop.type === EntityPropertyType.Tier) &&
            prop.options?.length
          ) {
            let isRelationship = false;
            lstOption = prop.options.map((x) => {
              const result: Option = {
                displayValue: x.text,
                value: x.id,
                extraData: {
                  ...x,
                },
              };
              if (prop.configs.fixedKey && prop.configs.fixedKey === FixedPropertyKey.CourtOrderType) {
                result.code = x.code;
              }
              if (prop.configs.fixedKey && prop.configs.fixedKey === FixedPropertyKey.StatusCourtOrder) {
                result.value = x.code;
              }
              if (x.relationshipType) {
                isRelationship = true;
              }
              return result;
            });
            if (isRelationship && (properties || [])) {
              let spouseId =  prop.options?.find((x :any) => x.relationshipType === RelationshipOptions.Spouse).id;
              if (control?.value) {
                this.currentRelationshipValue = prop.options?.find((x :any) => x.id === control.value).relationshipType;
              }
              if (control?.value !== spouseId || !control?.value) {
                this.entityPropertyService.checkExistSpouse({targetId: this.targetId, lookupTableId: prop.configs.lookupTable, value: spouseId})?.subscribe((result: any ) => {
                  if(result) {
                    this.spousePerson = deepClone(result);
                  }
                });
              }
            }
          } else if (
            (prop.type === EntityPropertyType.Employer || prop.type === EntityPropertyType.Department) &&
            prop.options?.length
          ) {
            lstOption = prop.options
              .filter((x) => x.active)
              .map((x) => ({
                displayValue: x.text,
                value: x.id,
                valueDescription: x.description,
              }));
          }

          if (stringToBoolean(prop.configs?.readOnly)) {
            formControl.disable();
          }

          // Check exists entity property value
          if (prop.configs?.unique === 'true'
            && prop.configs.autogenerated !== 'true'
            && prop.entityPropertyId.toLowerCase() !== PERSON_BASE_PROPERTIES.SSN.toLowerCase()
          ) {
            formControl.addAsyncValidators(
              checkApiValidator(
                this.entityPropertyService.checkExistPropertyValue,
                'value',
                undefined,
                {
                  params: {
                    entityId: prop.entityId,
                    componentId: prop.entityComponentId,
                    propertyId: prop.entityPropertyId,
                    id: control?.recordId,
                    propertyType: prop.type,
                  },
                },
                () => {
                  this.formGroup.updateValueAndValidity();
                },
              ),
            );
          }

          if (prop.staticPropertyType === StaticPropertyType.Percentage) {
            formControl.addAsyncValidators(
              checkApiValidator(
                this.entityPropertyService.checkSumOfPercentageValue,
                'value',
                undefined,
                {
                  params: {
                    targetId: this.targetId,
                    recordId: control?.recordId,
                  },
                },
                () => {
                  this.formGroup.updateValueAndValidity();
                },
              ),
            );
          }
          if (prop?.staticPropertyType === StaticPropertyType.IsPrimary) {
            this.entityPropertyService.isPrimary = control?.value;
          }

          if (prop.configs.fixedKey === FixedPropertyKey.LineOfDutyDeath) {
            formControl.addValidators(this._lineOfDutyDeathValidator());
            controlDetail.customError = 'invalidLineOfDutyDeath';
          }
          if (!this.isEdit) {
            if (prop.configs.fixedKey === FixedPropertyKey.RelationShip) {
              controlDetail.isHidden = true;
            }
            if (prop.configs.fixedKey === FixedPropertyKey.Payee) {
              formControl.disable();
            }
            if (prop.configs.fixedKey === FixedPropertyKey.StatusCourtOrder) {
              formControl.setValue(StatusCourtOrder.Pending);
            }
          }
          if (prop.configs.fixedKey === FixedPropertyKey.RejectReason) {
            controlDetail.isHidden = true;
          }
          controlDetail.type = propTransformedType;
          controlDetail.lstOption = lstOption;
          controlDetail.configs = prop.configs;
          controlDetail.data = prop;
          return controlDetail;
        }
      });
    //hide property Status
    if (this.isEdit) {
      this.controls = this.controls.filter((prop) => prop.type !== EntityPropertyType.Status);
    }

    this.controls.forEach((f) => {
      if (f.formControl !== undefined) {
        this.formGroup.setControl(f.name, f.formControl);
      } else {
        f.lstChildFormControl?.forEach((x) => {
          if (x.formControl !== undefined) {
            this.formGroup.setControl(f.name + x.name, x.formControl);
          }
        });
      }
    });
    this.propertyReferences = [...properties]
      .filter((prop) => prop.type === EntityPropertyType['Entity Reference'])
      .sort((a, b) => a.order - b.order)
      .map((item) => {
        return {
          ...item,
          referenceDatas: item.referenceDatas ?? [],
          initiationValue: item.initiationValue,
        };
      });
    this.propertyReferences.forEach((element: any) => {
      this.erControls[element.entityPropertyId] = [];
      this.erFormGroup[element.entityPropertyId] = new FormGroup({});
      this.erFormRadioButton[element.entityPropertyId] = new FormControl();
      this.selectedRecordId[element.entityPropertyId] = '';
    });

    if (this.isEdit && this.propertyReferences.length > 0) {
      this.propertyReferences.forEach((prop: any) => {
        prop.initiationValue = true;
        prop.referenceDatas = (
          this.data.filter(
            (item: any) =>
              item.entityId === prop.entityId &&
              item.entityComponentId === prop.entityComponentId &&
              item.entityPropertyId === prop.entityPropertyId,
          ) || []
        ).map((item: any) => {
          const entitySelectedId = item.entityReferenceLinkedId;
          return {
            ...item,
            value: this.getPropertyValue(item.propertyType, item.value),
            recordId: item.recordId,
            recordLinkedId: item.recordLinkedId,
            valueObj:
            item.propertyType === EntityPropertyType.Address || EntityPropertyType['Person Name']
            ? this.getPropertyValue(item.propertyType, item.value)
            : null,
            entitySelectedId: entitySelectedId,
            entityReferenceLinkedId: item.entityReferenceLinkedId,
            entityReferencePropertyId: item.entityReferencePropertyId,
            entityReferenceId: item.entityReferenceId,
            entityReferenceComponentId: item.entityReferenceComponentId,
          };
        });
      });
    }
    const isPrimaryControl = this.controls?.find(control => control?.staticPropertyType === StaticPropertyType.IsPrimary);
    if (!isPrimaryControl) {
      const percentageControl = this.controls?.find(
        (control) => control?.staticPropertyType === StaticPropertyType.Percentage,
      );
      percentageControl?.formControl?.clearAsyncValidators();
    }
  }

  isPrimaryChange(value: boolean) {
    const percentageField = this.controls?.find(control => control.staticPropertyType === StaticPropertyType.Percentage);
    this.entityPropertyService.isPrimary = value;

    if (percentageField?.formControl.value <= 100 && percentageField?.formControl.value >= 0) {
      if (value && percentageField?.formControl.valid) {
        percentageField?.formControl.updateValueAndValidity();
      } else {
        percentageField?.formControl.setErrors(null);
      }
    }
  }

  getPropertyValue(type: EntityPropertyType, value: any) {
    if (type === EntityPropertyType.Address || type === EntityPropertyType['Person Name']) {
      if (value && typeof value === 'object' && Object.values(value).every((item) => item === null))
        return (value = null);
      return (value = value);
    }
    return value;
  }

  setStatus(childForm: FieldData) {
    if (childForm.key === 'status') {
      this.listStatusEvent = this.listAllStatus
        ?.find((item) => item.id === childForm.formControl?.value)
        .events.filter((item: any) => this.isEdit || item.active)
        ?.map((item: any) => {
          return {
            value: item.id,
            displayValue: item.name,
          };
        });
    }
  }

  private _effectiveDate(fromElement: any, toElement: any) {
    return (c: AbstractControl) => {
      let fromElementCompare = fromElement?.formControl?.value ? ((typeof fromElement?.formControl?.value === 'string') ? new Date(fromElement?.formControl?.value) : fromElement?.formControl?.value) : '';
      let toElementCompare = toElement?.formControl?.value ? ((typeof toElement?.formControl?.value === 'string') ? new Date(toElement?.formControl?.value) : toElement?.formControl?.value) : '';
      return (fromElementCompare
        && toElementCompare
        && fromElementCompare >= toElementCompare) ? {errorMsg: 'System does not allow Effective From is equal OR greater than Effective To.'} : null;
    };
  }

  dateEffectiveChange(event: any, lstChildFormControl?: FieldData[], data?: FormControlDetail) {
    lstChildFormControl?.find((el) => el.key === 'effectFrom')?.formControl?.updateValueAndValidity();
    lstChildFormControl?.find((el) => el.key === 'effectTo')?.formControl?.updateValueAndValidity();
    this.validateEffectForm(data);
    this.validateAddressField(data);
  }

  dateValueChange(event: any, form: any): void {
    if (this.datePropValidation) {
      //validate property rules
      const validateRules = this.datePropValidation.filter((o) => o.property === form.name);
      if (validateRules) {
        for (const element of validateRules) {
          const rule = element;
          const compareProperty = rule.validationProp;

          const compareForm = this.controls?.find((o) => o.name === compareProperty);
          if (compareForm) {
            const compareControl = compareForm.formControl;
            if (compareControl) {
              const valueCompare = compareControl.value;
              if (valueCompare) {
                if (rule.operator === ValidationOperator.LessThan) {
                  let dateValidation = new Date(valueCompare);
                  dateValidation = this.compareMinDateValidation(form.name, dateValidation);
                  this.setMinDate(form.formControl, dateValidation);
                }

                if (rule.operator === ValidationOperator.GreaterThan) {
                  let dateValidation = new Date(valueCompare);
                  dateValidation = this.compareMaxDateValidation(form.name, dateValidation);
                  this.setMaxDate(form.formControl, dateValidation);
                }
                this._setDateValidationMessage(form.formControl);
              }
            }
          }
        }
      }

      //validate property
      const validateProps = this.datePropValidation.filter((o) => o.validationProp == form.name);

      validateProps.forEach((prop) => {
        const sourceForm = this.controls?.find((o) => o.name === prop.property);
        if (sourceForm) {
          if (prop.operator === ValidationOperator.LessThan && event) {
            let valDate = new Date(event);
            valDate = this.compareMaxDateValidation(prop.property, valDate);
            this.setMaxDate(sourceForm, valDate);
          }

          if (prop.operator === ValidationOperator.GreaterThan && event) {
            let valDate = new Date(event);
            valDate = this.compareMinDateValidation(prop.property, valDate);
            this.setMinDate(sourceForm, valDate);
          }

          this._setDateValidationMessage(sourceForm);
        }
      });
    }
  }

  private compareMaxDateValidation(property: string, dateVal: Date): Date {
    if (this.dateValidationValue?.length > 0) {
      const val = this.dateValidationValue?.find((o) => o.property === property);
      if (val && val.max) {
        dateVal.setHours(0);
        if (val.max < dateVal) {
          dateVal = val.max;
        }
      }
    }
    return dateVal;
  }

  private compareMinDateValidation(property: string, dateVal: Date): Date {
    if (this.dateValidationValue?.length > 0) {
      const val = this.dateValidationValue?.find((o) => o.property === property);
      if (val && val.min) {
        dateVal.setHours(0);
        if (val.min > dateVal) {
          dateVal = val.min;
        }
      }
    }
    return dateVal;
  }

  private setMaxDate(formControlDetail: FormControlDetail, vaidationDate: Date): void {
    if (vaidationDate) {
      vaidationDate.setHours(-24);

      formControlDetail.max = vaidationDate;
    }
  }

  private setMinDate(formControlDetail: FormControlDetail, vaidationDate: Date): void {
    if (vaidationDate) {
      vaidationDate.setHours(24);
      formControlDetail.min = vaidationDate;
    }
  }

  private _setDateValidationMessage(formControlDetail: FormControlDetail): void {
    const min = formControlDetail.min as Date;
    const max = formControlDetail.max as Date;
    const current = new Date();

    min?.setHours(0, 0, 0, 0);
    max?.setHours(0, 0, 0, 0);
    current.setHours(0, 0, 0, 0);

    formControlDetail.minMessage = formControlDetail.maxMessage = 'The input does not meet the validation.';

    if (min?.getTime() === current.getTime()) {
      formControlDetail.minMessage = 'The input must be greater than today';
    }

    if (max?.getTime() === current.getTime()) {
      formControlDetail.maxMessage = 'The input must be less than today';
    }

    if (max && min && min.getTime() >= max.getTime()) {
      formControlDetail.maxMessage = formControlDetail.minMessage = 'No date satifies the validation.';
    }
  }

  checkFieldsUnique(result: EntityInitiationPropertyValue[], entityPropertyId: string) {
    // Add validation about check duplicate for unique fields
    result?.forEach((item) => {
      if (item.configs?.unique === 'true') {
        let control = this.controls?.find((control) => control.name === item.entityPropertyId);
        if (control) {
          if (!this.validatorPrevious[entityPropertyId]) {
            this.validatorPrevious[entityPropertyId] = {};
          }
          if (
            this.validatorPrevious[entityPropertyId] &&
            this.validatorPrevious[entityPropertyId][item.entityPropertyId]
          ) {
            control?.formControl.removeValidators(this.validatorPrevious[entityPropertyId][item.entityPropertyId]);
          }
          let type = typeof item.value;
          if (item.value && type === 'object' && item.value.originalValue) {
            this.validatorPrevious[entityPropertyId][item.entityPropertyId] = checkUnique(
              item.value.originalValue,
              item?.label + ' is already exists.',
            );
            control?.formControl.addValidators(this.validatorPrevious[entityPropertyId][item.entityPropertyId]);
          } else if (item.value && type !== 'object') {
            this.validatorPrevious[entityPropertyId][item.entityPropertyId] = checkUnique(
              item.value,
              item?.label + ' is already exists.',
            );
            control?.formControl.addValidators(this.validatorPrevious[entityPropertyId][item.entityPropertyId]);
          }
          control?.formControl.updateValueAndValidity();
        }
      }
    });
  }

  createUniqueOtherReference(property: InitiationProperty) {
    // Create uniqueOtherReference for check duplicate all fields unique from Other Reference
    let uniqueOtherReference: { parent: string; name: string; value: any }[] = [];
    this.propertyReferences
      .filter((el: InitiationProperty) => el.entityPropertyId !== property.entityPropertyId)
      .forEach((el: InitiationProperty) => {
        el.referenceDatas
          ?.filter((item) => item.configs?.unique === 'true')
          .forEach((item) => {
            let type = typeof item.value;
            uniqueOtherReference.push({
              parent: el.entityPropertyId,
              name: item.entityPropertyId,
              value: type === 'object' && item.value != null ? item.value.originalValue : item.value,
            });
          });
      });
    return uniqueOtherReference;
  }

  onSubmit(): void {
    this.formSubmit$
      .pipe(
        tap(() => {
          this.formGroup.markAllAsTouched();
          const arrForm = Object.keys(this.erFormGroup);
          for (const element of arrForm) {
            const form = this.erFormGroup[element];
            form.markAllAsTouched();
          }
        }),
        debounceTime(500),
        switchMap(() => {
          const arrayForm = Object.keys(this.erFormGroup);
          return forkJoin([
            this.formGroup.statusChanges.pipe(
              startWith(this.formGroup.status),
              filter((status) => status !== AbstractControlStatus.PENDING),
              take(1),
            ),
            ...arrayForm.map((i) => {
              return this.erFormGroup[i].statusChanges.pipe(
                startWith(this.erFormGroup[i].status),
                filter((status) => status !== AbstractControlStatus.PENDING),
                take(1),
              );
            }),
          ]);
        }),
        filter(
          (status) =>
            status.filter((i) => i === AbstractControlStatus.VALID || i === AbstractControlStatus.DISABLED).length ===
              status.length && !this.isSubmitting,
        ),
      )
      .subscribe(() => {
        if (this.isRecordExisted) {
          return;
        }
        this.saveValue();
      });
  }

  private saveEntityValue(entityPropertyId: string) {
    const controls = this.erControls[entityPropertyId];
    const entityPropertyValues: EntityInitiationPropertyValue[] = [];
    const datepipe = new DatePipe('en-US');
    if (this.erFormGroup[entityPropertyId].status === AbstractControlStatus.DISABLED) {
      return entityPropertyValues;
    }
    for (const f of controls) {
      let value = f.formControl?.value;
      let valueObj: any;
      let valueMask = {
        maskedValue: '',
        originalValue: '',
      };
      switch (f.type) {
        case EntityPropertyType.Status:
        case EntityPropertyType.Address:
        case EntityPropertyType['Person Name']:
          const objVal: any = {};
          let isHaveData = false;
          for (const childForm of f.lstChildFormControl || []) {
            if (childForm.formControl?.value) {
              isHaveData = true;
            }
            if (childForm.type === 'date') {
              objVal[childForm.name] = childForm.formControl?.value
                ? datepipe.transform(childForm.formControl?.value, 'yyyy-MM-dd')
                : f.type === EntityPropertyType.Address
                  ? null
                  : '';
            } else if (childForm.key === 'state') {
              const country = f.lstChildFormControl?.find((el: any) => el.key === 'country');
              const selectedCountryCode = country?.lstOption?.find((el: any) => el.value === country.formControl?.value)?.code;
              if (!['USA', 'CA', 'CAN'].includes(selectedCountryCode)) {
                objVal.StateName = childForm.formControl?.value;
                const countryCodeSource = country?.lstOption?.find((item: any) => item.value === this.data?.find((el: any) => el.type === EntityPropertyType.Address)?.value?.country)?.code;
                if (countryCodeSource && !['USA', 'CA', 'CAN'].includes(countryCodeSource)) {
                  objVal.State = this.data.find((el: any) => el.type === EntityPropertyType.Address)?.value?.state ?? null;
                } else {
                  objVal.State = null;
                }
              } else {
                objVal.StateName = null;
                objVal[childForm.name] = childForm.formControl?.value;
              }
            } else {
              objVal[childForm.name] = childForm.formControl?.value;
            }
          }
          const codeAndId = {
            code: f.data.addressCode,
            id: f.data.addressId
          };
          if (!this.isAddNew && f.type === EntityPropertyType.Address) {
            Object.assign(objVal, codeAndId);
          }
          value = isHaveData ? JSON.stringify(objVal) : JSON.stringify(codeAndId);
          valueObj = value ? JSON.parse(value, (key, value) => toCamelCase(key, value)) : '';
          break;
        case EntityPropertyType.Date:
          value = value ? datepipe.transform(value, 'yyyy-MM-dd') : '';
          break;
        case EntityPropertyType.Binary:
          value = value ? true : false;
          break;
        case EntityPropertyType.Percentage:
          value = value?.toString() || '';
          value = (value?.endsWith('.') ? value?.substring(0, value.length - 1) : value) || '';
          break;
        case EntityPropertyType.Email:
          value = {
            originalValue: value,
          };
          break;
        case EntityPropertyType.SSN:
          value = {
            originalValue: value,
          };
          break;
        case EntityPropertyType.Phone:
          value = {
            originalValue: value,
          };
          break;
        default:
          value = isEmpty(value) ? '' : value?.toString();
      }

      entityPropertyValues.push({
        configs: f.data.configs,
        type: f.data.type,
        label: f.data.label,
        options: f.data.options,
        entitySelectedId: this.erFormRadioButton[entityPropertyId].value,
        entityId: f.data.entityId,
        entityComponentId: f.data.entityComponentId,
        entityPropertyId: f.data.entityPropertyId,
        value: value,
        valueObj: valueObj,
        valueMask: valueMask,
        recordId: f.recordId,
        selectedRecordId: this.selectedRecordId[entityPropertyId],
        isInitiationProperty: f.data.isInitiationProperty,
        isComputedProperty: f.data.isComputedProperty,
      } as EntityInitiationPropertyValue);
    }
    return entityPropertyValues;
  }
  private saveValue(): void {
    this.entityReferenceRequiredMessage = '';
    const array = Object.keys(this.erControls);
    for (const element of array) {
      const entityPropertyId = element;
      const result: any = this.saveEntityValue(entityPropertyId);
      let propRef = this.propertyReferences?.find((item: any) => item.entityPropertyId === entityPropertyId);
      if (propRef) {
        propRef.initiationValue = false;
        propRef.referenceDatas = result;
      }
      this.checkFieldsUnique(result, entityPropertyId);
    }
    const entityPropertyValues: EntityInitiationPropertyValue[] = [];
    const datepipe = new DatePipe('en-US');
    for (const f of this.controls) {
      let value = f.formControl?.value;
      let rawValue = f.formControl?.value;
      switch (f.type) {
        case EntityPropertyType.Status:
        case EntityPropertyType.Address:
        case EntityPropertyType['Person Name']:
          const objVal: any = {};
          let isHaveData = false;
          for (const childForm of f.lstChildFormControl || []) {
            if (childForm.formControl?.value) {
              isHaveData = true;
            }
            if (childForm.type === 'date') {
              objVal[childForm.name] = childForm.formControl?.value
                ? datepipe.transform(childForm.formControl?.value, 'yyyy-MM-dd')
                : f.type === EntityPropertyType.Address
                  ? null
                  : '';
            } else if (childForm.key === 'state') {
              const country = f.lstChildFormControl?.find(el => el.key === 'country');
              const selectedCountryCode = country?.lstOption?.find(el => el.value === country.formControl?.value)?.code;
              if (!['USA', 'CA', 'CAN'].includes(selectedCountryCode)) {
                objVal.StateName = childForm.formControl?.value;
                const countryCodeSource = country?.lstOption?.find((item: any) => item.value === this.data?.find((el: any) => el.type === EntityPropertyType.Address)?.value?.country)?.code;
                if (countryCodeSource && !['USA', 'CA', 'CAN'].includes(countryCodeSource)) {
                  objVal.State = this.data.find((el: any) => el.type === EntityPropertyType.Address)?.value?.state ?? null;
                } else {
                  objVal.State = null;
                }
              } else {
                objVal.StateName = null;
                objVal[childForm.name] = childForm.formControl?.value;
              }
            } else {
              objVal[childForm.name] = childForm.formControl?.value;
            }
          }
          const codeAndId = {
            code: f.code,
            id: f.data.addressId
          };
          value = isHaveData
            ? JSON.stringify({ ...objVal, ...codeAndId })
            : JSON.stringify({ ...codeAndId });
          break;
        case EntityPropertyType.Date:
          value = value ? datepipe.transform(value, 'yyyy-MM-dd') : null;
          break;
        case EntityPropertyType.Binary:
          value = value ? true : false;
          break;
        case EntityPropertyType.Percentage:
          value = value?.toString() || null;
          value = (value?.endsWith('.') ? value?.substring(0, value.length - 1) : value) || null;
          break;
        case EntityPropertyType.SSN:
        case EntityPropertyType.Phone:
          value = getValueWithoutFormat(value);
          break;
        default:
          value = value ? value?.toString() : null;
      }
      // Not add Identifier Auto Generated to body request when save (act as label not form control)
      if (
        !f.isIdentifierAutoGeneratedPropertyType &&
        ((!this.isEdit && value !== null && value !== undefined && value !== '') || this.isEdit)
      ) {
        entityPropertyValues.push({
          recordId: f.recordId,
          entityId: f.data.entityId,
          entityComponentId: f.data.entityComponentId,
          entityPropertyId: f.data.entityPropertyId,
          value: value,
          type: f.type,
          isComputedProperty: f.data.isComputedProperty,
          rawValue,
          isRequired: f.isRequired,
        } as EntityInitiationPropertyValue);
      }
    }
    for (const p of this.propertyReferences) {
      if (p.referenceDatas && p.referenceDatas.length > 0) {
        const properties = p.referenceDatas;
        const entityPropertyReferencesValues: EntityInitiationPropertyValue[] = [];
        const entitySelectedId = properties?.find(
          (item: any) => item.entitySelectedId !== null && item.entitySelectedId !== undefined,
        )?.entitySelectedId;
        const selectedRecordId =
          properties?.find((item: any) => item.selectedRecordId !== null && item.selectedRecordId !== undefined)
            ?.selectedRecordId || properties[0]?.recordLinkedId;
        for (const f of properties) {
          let value =
            f.value?.originalValue || f.value?.originalValue === null || f.value?.originalValue === ''
              ? f.value?.originalValue
              : f.value;
          if (f.type === EntityPropertyType.SSN || f.type === EntityPropertyType.Phone) {
            value = value ? getValueWithoutFormat(value) : value;
          }
          if ((!this.isEdit && value !== null && value !== undefined && value !== '') || this.isEdit) {
            entityPropertyReferencesValues.push({
              entityId: f.entityId,
              recordId: f.recordId,
              entityComponentId: f.entityComponentId,
              entityPropertyId: f.entityPropertyId,
              value: value,
              isComputedProperty: f.isComputedProperty,
            } as EntityInitiationPropertyValue);
          }
        }

        if (entityPropertyReferencesValues.length > 0) {
          entityPropertyValues.push({
            entityId: p.entityId,
            entityComponentId: p.entityComponentId,
            entityPropertyId: p.entityPropertyId,
            entityPropertyReferenceValue: {
              recordId: selectedRecordId,
              entityId: entitySelectedId,
              entityPropertyValues: entityPropertyReferencesValues,
            },
          } as any);
        }
      } else if (p.entityComponentId === ENTITY_BENEFICIARIES_LIST_GUID
        || p.entityComponentId === ENTITY_DEPENDENTS_LIST_GUID
        || p.entityComponentId === ENTITY_POWER_OF_ATTORNEY_LIST_GUID
      ) {
        this.entityReferenceRequiredMessage = `${p.label} is required.`
        return;
      }
    }
    if (this.isEffectiveData && !this.cardInfor?.isList && this.listMemberParticipants.includes(this.entityId)) {
      entityPropertyValues.forEach(item => {
        if (item.type === EntityPropertyType.Address) {
          const getAddressId = this.data?.find((value: any) => value.entityPropertyId === item.entityPropertyId);
          if (getAddressId?.value?.id) {
            const cloneValue = item.value ? JSON.parse(item.value) : null;
            if (cloneValue) {
              cloneValue.id = getAddressId.value.id;
              cloneValue.code = getAddressId.value.code;
              item.value = JSON.stringify(cloneValue);
            }
          }
        }
      });
    }
    const request = {
      entityId: this.entityId,
      entityPropertyValues: [...entityPropertyValues],
    };
    if (this.dateOfDeathControl?.value) {
      (request as any)[PERSON_BASE_PROPERTIES.ReportedDeceased.toLowerCase()] = true;
    }
    if (this.isEffectiveData && !this.cardInfor?.isList && this.listMemberParticipants.includes(this.entityId)) {
      const cloneTypeAddress = entityPropertyValues.filter((item) => item.type === EntityPropertyType.Address);
      if (!cloneTypeAddress.length) {
        this.updateEvent(request);
        return;
      }
      let body: any = [];
      cloneTypeAddress.forEach((item) => {
        const value = item.value ? JSON.parse(item.value) : null;

        // New address
        if (!value.id && !value.code) {
          return;
        }
        // If the property had toggle “Required” = “OFF”
        if (!item.isRequired
          // if <selected Country> <> null but all other component is null, then system allow this input and consider the address = null when saved.
          && Object.entries(value)
            .filter(([key, v]) => !['id', 'code', 'Country'].includes(key))
            .every((([key, v]) => !v))
        ) {
          return;
        }

        if (value && Object.keys(value).length > 1) {
          const data = {
            reCordId: item.recordId ? item.recordId : null,
            entityId: item.entityId,
            key: item.entityPropertyId,
            entityPropertyId: item.entityPropertyId,
            effectFrom: value.EffectFrom,
            effectTo: value.EffectTo || null,
            id: value?.id ? value.id : null,
          };
          body.push(data);
        }
      });
      this.memberStore.dispatch(AddressHistoryAction.checkExitAddress({ body }));
      let subscription = this.memberStore
      .pipe(
        select(fromMember.selectAddressHistoryExitState),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((state) => {
        if (state?.messages) {
          if (state?.messages.length) {
            state?.messages?.forEach((item: any) => {
              const checkAddressError = this.controls.find(value => value.data.entityPropertyId === item.key);
              if (checkAddressError) {
                const form = checkAddressError?.lstChildFormControl?.find(f => f.key === 'effectFrom')!;
                form.messageError = item.message;
                form?.formControl?.setErrors({inValidAsync:true});
              }
            });
          } else {
            this.updateEvent(request);
          }
          this.memberStore.dispatch(clearSetMemberEventStateAction());
          subscription.unsubscribe();
        }
      });
    } else {
      this.updateEvent(request);
    }
  }

  updateEvent(data: any) {
    if (this.isEdit) {
      const editEmitter = (request: any) => this.editSectionEvent.emit(request);
      const validatePaymentInProgress = (request: any) => this._validatePaymentInProgress(request, editEmitter);
      const validateCourtOrder = (request: any) => this._validateCourtOrderStatus(request, validatePaymentInProgress);
      this._showConfirmPopupRetireRehire(data, validateCourtOrder);
    } else {
      this.addNewEvent.emit(data);
    }
  }

  onClickIcon(property: any) {
    property.isMasked = property.isMasked === undefined ? false : !property.isMasked;
  }

  onCancel(): void {
    this.cancelEvent.emit();
  }

  get dateOfDeathControl(): FormControl {
    return this._getControlByFixedKey(FixedPropertyKey.DateOfDeath);
  }

  get reportedDeceasedControl(): FormControl {
    return this.formGroup.get(PERSON_BASE_PROPERTIES.ReportedDeceased.toLowerCase()) as FormControl;
  }

  get lineOfDutyDeathControl(): FormControl {
    return this._getControlByFixedKey(FixedPropertyKey.LineOfDutyDeath);
  }

  get newHireExamControl(): FormControl {
    return this._getControlByFixedKey(FixedPropertyKey.NewHireExam);
  }

  get newHireExamDateControl(): FormControl {
    return this._getControlByFixedKey(FixedPropertyKey.NewHireExamDate);
  }

  get retireRehireControl(): FormControl {
    return this._getControlByFixedKey(FixedPropertyKey.RetireRehire);
  }

  get courtOrderTypeControl(): FormControl {
    return this._getControlByFixedKey(FixedPropertyKey.CourtOrderType);
  }

  get statusCourtOrderControl(): FormControl {
    return this._getControlByFixedKey(FixedPropertyKey.StatusCourtOrder);
  }

  get payeeControl(): FormControl {
    return this._getControlByFixedKey(FixedPropertyKey.Payee);
  }

  get inactivationDateControl(): FormControl {
    return this._getControlByFixedKey(EMPLOYER_FIXED_PROPERTIES.InactivationDate);
  }

  get inactiveControl(): FormControl {
    return this._getControlByFixedKey(EMPLOYER_FIXED_PROPERTIES.Inactive);
  }

  private _getControlByFixedKey(propertyKey: string): FormControl {
    const entityPropety = this._findPropertyByFixedKey(this.properties, propertyKey);
    return this.formGroup.get(entityPropety?.entityPropertyId ?? '') as FormControl;
  }

  private _findPropertyByFixedKey(listProperty: any[], propertyKey: string) {
    return listProperty?.find((x) => x.configs?.fixedKey === propertyKey);
  }

  private _lineOfDutyDeathValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) return null;
      if (control.value && !this.dateOfDeathControl?.value) {
        return {
          invalidLineOfDutyDeath: 'Invalid Line of Duty Death.',
        };
      }

      return null;
    };
  }

  private _onChangeDateOfDeathValue() {
    this.dateOfDeathControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.lineOfDutyDeathControl?.updateValueAndValidity();
      if (value) {
        this.reportedDeceasedControl?.setValue(true);
      }
      this.reportedDeceasedControl?.updateValueAndValidity();
    });
  }

  private _onChangeNewHireExamValue() {
    this.newHireExamControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((isNewHireExam) => {
      if (isNewHireExam) {
        this.newHireExamDateControl.enable();
      } else {
        this.newHireExamDateControl.disable();
        this.newHireExamDateControl.reset();
      }
      const propetyNewHireExamDate = this._findPropertyByFixedKey(this.properties, FixedPropertyKey.NewHireExamDate);
      const newHireExamDate = this.controls?.find((x) => x.name === propetyNewHireExamDate?.entityPropertyId);
      newHireExamDate!.isHidden = !isNewHireExam;
      newHireExamDate!.isRequired = isNewHireExam;
    });
  }

  private _checkIsCallApiConfirmRetireRehire(): boolean {
    const retireRehireProperty = this._findPropertyByFixedKey(this.data, FixedPropertyKey.RetireRehire);
    return !!(this.isEdit &&
      this.targetId &&
      retireRehireProperty &&
      this.retireRehireControl &&
      !this.retireRehireControl.value &&
      this.retireRehireControl.value !== retireRehireProperty.value);
  }

  private _validateCourtOrderStatus(data: any, callback: (request: any) => void): void {
    if (!this._findPropertyByFixedKey(this.properties, FixedPropertyKey.StatusCourtOrder)) {
      callback(data);
      return;
    }
    if (![StatusCourtOrder.Terminated, StatusCourtOrder.Rejected].includes(this.statusCourtOrderControl.value)) {
      callback(data);
      return;
    }
    const courtOrderTypeProperty = this._findPropertyByFixedKey(this.properties, FixedPropertyKey.CourtOrderType);
    const caseNumberControl = this.formGroup.get(FixedPropertyId.CaseNumber.toLowerCase()) as FormControl;
    if (
      this.isEdit &&
      this.targetId &&
      courtOrderTypeProperty &&
      caseNumberControl &&
      this.courtOrderTypeControl.value !== courtOrderTypeProperty.value
    ) {
      this.entityDataService
        .validateCourtOrder({
          memberId: this.targetId,
          caseNumber: caseNumberControl.value,
          courtOrderType:
            courtOrderTypeProperty.code === COURT_ORDER_TYPE_CODE.QDRO
              ? CourtOrderType.QDRO
              : CourtOrderType.Garnishment,
        })
        .subscribe((response) => {
          if (response.isChangedStatus) {
            callback(data);
            return;
          }
          const text =
            response.courtOrderType === CourtOrderType.Garnishment
              ? 'The garnishment has been setup as a deduction. Please remove the deduction from the benefit before terminating this court order.'
              : 'The QDRO is still being collected. Please remove the deduction and terminate the QDRO benefit before terminating this court order.';
          this.dialog.open(ConfirmPopupComponent, {
            panelClass: 'confirm-popup',
            data: {
              title: 'Warning',
              text,
              type: ConfirmType.Warning,
              hideConfirmButton: true,
              cancelButtonTitle: 'Close',
            },
          });
        });
      return;
    }
    callback(data);
  }

  private _showConfirmPopupRetireRehire(request: any, callback: (request: any) => void) {
    if (!this._checkIsCallApiConfirmRetireRehire()) {
      callback(request);
      return;
    }
    const newRetireRehire = this.retireRehireControl?.value;
    this.entityDataService.confirmRetireRehire(this.targetId, newRetireRehire).subscribe((res) => {
      if (res?.isShowConfirmPopup) {
        const message = newRetireRehire
          ? 'This participant was included in Annual Certification(s). Do you want to remove it from Annual Certification(s)?'
          : 'This participant was not included in unposted Annual Certification(s). This participant can be added back to Annual Certification(s) where eligible and Annual Certification Status will be reset to "1 - Not Complete". Do you want to include?';
        const dialogRef = this.dialog.open(ConfirmPopupComponent, {
          panelClass: 'confirm-popup',
          data: {
            title: 'Confirmation',
            text: message,
            type: ConfirmType.ConfirmSave,
            saveButtonTitle: 'Yes',
            cancelButtonTitle: 'No',
            hideSaveAsButton: true,
            hideConfirmButton: true,
          },
        });
        dialogRef.afterClosed().subscribe((result) => {
          request.isConfirmRetireRehire = !!result;
          callback(request);
        });
      } else {
        callback(request);
      }
    });
  }

  private _validateInactivationDate(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value || control.pristine !== false) {
        return of(null);
      }
      const inactivationDateValue = new DatePipe('en-US').transform(control.value, 'yyyy-MM-dd');

      if (inactivationDateValue === null) {
        return of(null);
      }

      const body: EmployerCheckDisableRequest = {
        employerId: this.targetId ?? '',
        inactivationDate: inactivationDateValue ?? '',
      };
      return timer(100).pipe(
        switchMap(
          (): Observable<ValidationErrors | null> =>
            this.entityDataService.getMunicipalityCheckDisable(body).pipe(
              map((res: any) => {
                if (res?.isError) {
                  return { dateValidationError: res?.message };
                }
                if (this.inactiveControl?.value === true) {
                  return null;
                }
                if (this.currentInactivationDate) {
                  this.inactiveControl?.setValue(true);
                  return null;
                }
                const dialogRef = this.dialog.open(ConfirmPopupComponent, {
                  panelClass: 'confirm-popup',
                  data: {
                    title: 'Confirmation',
                    text: res.message,
                    type: ConfirmType.ConfirmSave,
                    saveButtonTitle: 'Yes',
                    cancelButtonTitle: 'Cancel',
                    hideSaveAsButton: true,
                    hideConfirmButton: true,
                  },
                });
                dialogRef.afterClosed().subscribe((result: any) => {
                  if (!result) {
                    control.setValue(this.currentInactivationDate);
                    return;
                  }
                  this.inactiveControl?.setValue(true);
                });
                return null;
              }),
              finalize(() => {
                this.formGroup.updateValueAndValidity();
              }),
            ),
        ),
      );
    };
  }

  private _validateDateOfBirth(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value || control.pristine !== false) {
        return of(null);
      }
      const dateofBirthValue = new DatePipe('en-US').transform(control.value, 'yyyy-MM-dd');

      if (dateofBirthValue === null) {
        return of(null);
      }

      const body: EmployerCheckDateOfBirthRequest = {
        memberId: this.targetId ?? '',
        dateOfBirth: dateofBirthValue ?? '',
      };
      return timer(100).pipe(
        switchMap(
          (): Observable<ValidationErrors | null> =>
            this.entityDataService.getMunicipalityCheckDateOfBirth(body).pipe(
              map((res: any) => {
                if (res?.isError) {
                  return { errorMsg: res?.message };
                }
                return null;
              }),
              finalize(() => {
                this.formGroup.updateValueAndValidity();
              }),
            ),
        ),
      );
    };
  }

  private _onChangeInactivationDateValue() {
    this.inactivationDateControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((inactivationDate) => {
      if (!this.currentInactivationDate && !inactivationDate) {
        this.inactiveControl?.setValue(false);
        return;
      }
      if (this.currentInactivationDate && this.inactivationDateControl.valid && !inactivationDate) {
        const dialogRef = this.dialog.open(ConfirmPopupComponent, {
          panelClass: 'confirm-popup',
          data: {
            title: 'Confirmation',
            text: 'This Municipality is inactive. Are you sure you want to reactivate this Municipality?',
            type: ConfirmType.ConfirmSave,
            saveButtonTitle: 'Yes',
            cancelButtonTitle: 'Cancel',
            hideSaveAsButton: true,
            hideConfirmButton: true,
          },
        });
        dialogRef.afterClosed().subscribe((result: any) => {
          if (!result) {
            this.inactivationDateControl?.setValue(this.currentInactivationDate);
            return;
          }
          this.inactiveControl?.setValue(false);
        });
      }
    });
  }

  checkTextBox(data?: FieldData[]) {
    const checkCountry = data?.find((item: FieldData) => item.key === 'country');
    const checkCode = checkCountry?.lstOption?.find((item: any) => item.value === checkCountry?.formControl?.value)!;
    return checkCode?.code;
  }

  changeValueAddress(field: FieldData, fieldDatas?: FieldData[], data?: FormControlDetail) {
    if (field.key === 'country') {
      const selectedCountryCode = field.lstOption?.find(el => el.value === field.formControl?.value)?.code;
      const state = data?.lstChildFormControl?.find(el => el.key === 'state');
      const zipCode = data?.lstChildFormControl?.find(el => el.key === 'zipCode');
      if (state && !['USA', 'CA', 'CAN'].includes(selectedCountryCode)) {
        state.type = 'text';
        state.required = false;
        state.isSetRequired = false;
        if (zipCode) {
          zipCode.required = false;
          zipCode.isSetRequired = false;
        }
        setTimeout(() => {
          state.formControl?.removeValidators(Validators.required);
          state.formControl?.setValue(null);
          state?.formControl?.updateValueAndValidity();
          zipCode?.formControl?.clearValidators();
          zipCode?.formControl?.setErrors(null);
          zipCode?.formControl?.updateValueAndValidity();
        })
      } else if (state && state.type === 'text') {
        state.type = 'select';
        state.formControl?.setValue(null);
      }

      if (state && ['CA', 'CAN'].includes(selectedCountryCode)) {
        setTimeout(() => {
          state.isSetRequired = true;
          zipCode?.formControl?.removeValidators(Validators.required);
          zipCode?.formControl?.updateValueAndValidity({emitEvent: false});
          if (zipCode) {
            zipCode.isSetRequired = false;
            zipCode.required = false;
          }
          const err = deepClone(zipCode?.formControl?.errors);
          delete err?.required;
          zipCode?.formControl?.setErrors(err ?? null);
        });
      }

      if (state && zipCode && ['USA'].includes(selectedCountryCode)) {
        setTimeout(() => {
          state.isSetRequired = true;
          zipCode.isSetRequired = true;
        });
      }

      if (state) {
        state.formControl?.setValue(null);
      }
    }
    this.validateEffectForm(data);
    this.validateAddressField(data);
  }

  changeValueInputAddress(data: FormControlDetail) {
    this.validateEffectForm(data);
    this.validateAddressField(data);
  }

  validateEffectForm(data?: FormControlDetail) {
    if (!this.isEffectiveData || data?.isRequired) return;
    const effectFrom = data?.lstChildFormControl?.find((item: FieldData) => item?.key === 'effectFrom');
    if (effectFrom) {
      // ​“Effective From” is required when one of other fields (except for Effective To) has value and Required = OFF.
      effectFrom.isSetRequired = data?.lstChildFormControl?.some(c => !['country', 'effectTo'].includes(c.key) && c.formControl?.value);
    }
  }

  validateAddressField(data?: FormControlDetail) {
    if (data?.isRequired) return;
    // If the property had toggle “Required” = “OFF”
    const checkValueForm = data?.lstChildFormControl?.find((item: FieldData) => item?.formControl?.value);
    const country = data?.lstChildFormControl?.find((item: FieldData) => item?.key === 'country');
    const selectedCountryCode = country?.lstOption?.find(el => el.value === country.formControl?.value)?.code;
    
    // If <selected Country> <> null but all other component is null, then system allow this input
    const allOtherComponentsNull = data?.lstChildFormControl?.filter(c => c.key !== 'country').every(c => !c.formControl?.value);
    
    data?.lstChildFormControl?.forEach((item: FieldData) => {
      if (!['effectFrom', 'effectTo', 'street2'].includes(item.key)) {
        item.isSetRequired = (checkValueForm && !allOtherComponentsNull) ? true : false;
        if (item.isSetRequired) {
          item?.formControl?.addValidators(Validators.required);
        } else {
          item?.formControl?.removeValidators(Validators.required);
        }

        if ((selectedCountryCode !== 'USA' && item?.key === 'zipCode') || (item?.key === 'state' && !['USA', 'CA', 'CAN'].includes(selectedCountryCode))) {
          item?.formControl?.removeValidators(Validators.required);
        }
        
        item?.formControl?.updateValueAndValidity();
      }
    });
  }

  changeLookup(data: FormControlDetail, value: any) {
    if (data?.configs?.fixedKey === FixedPropertyKey.StatusCourtOrder) {
      const selectedStatusCourtOrder = data?.lstOption?.find((item: any) => item.value === data?.formControl.value);
      const checkRejectReason = this.controls?.find(item => item.configs?.fixedKey === FixedPropertyKey.RejectReason);
      if (checkRejectReason) {
        const listRejectReason = [StatusCourtOrder.Terminated, StatusCourtOrder.Rejected];
        checkRejectReason.isHidden = listRejectReason.includes(selectedStatusCourtOrder.value) ? false : true;
        checkRejectReason.isRequired = listRejectReason.includes(selectedStatusCourtOrder.value) ? true : false;
        checkRejectReason?.formControl.setValue('');
        if (listRejectReason.includes(selectedStatusCourtOrder.value)) {
          checkRejectReason?.formControl.addValidators(Validators.required);
        } else {
          checkRejectReason?.formControl.removeValidators(Validators.required);
        }
        checkRejectReason?.formControl.updateValueAndValidity();
      }
    }

    if (data?.configs?.fixedKey === FixedPropertyKey.CourtOrderType) {
      const selectedCourtOrder = data?.lstOption?.find((item: any) => item.value === data?.formControl.value);
      this.payeeControl?.enable();
      const body: PayeeRequest = {
        memberId: this.targetId,
        courtOrderType:
          selectedCourtOrder.code === COURT_ORDER_TYPE_CODE.QDRO ? CourtOrderType.QDRO : CourtOrderType.Garnishment,
        screenId: this.screenId,
        courtOrderComponentId: this.entityComponentId,
        courtOrderId: this.recordId
      };
      if (this.isEdit) {
        body.payeeId = this.payeeId;
      }
      const checkPayee = this.controls?.find(item => item.configs?.fixedKey === FixedPropertyKey.Payee);
      if (checkPayee) {
        checkPayee.lstOption = [];
        checkPayee?.formControl.setValue('');
      }
      this.memberStore.dispatch(getPayeePropertiesAction({
        body
      }));
      const checkRelationship = this.controls?.find(item => item.configs?.fixedKey === FixedPropertyKey.RelationShip);
      this.relationshipLookup = checkRelationship;
      if (checkRelationship) {
        checkRelationship.isHidden = selectedCourtOrder.code !== COURT_ORDER_TYPE_CODE.QDRO;
        checkRelationship.isRequired = selectedCourtOrder.code === COURT_ORDER_TYPE_CODE.QDRO;
        checkRelationship?.formControl.setValue('');
        if (selectedCourtOrder.code === COURT_ORDER_TYPE_CODE.QDRO) {
          checkRelationship?.formControl.addValidators(Validators.required);
        } else {
          checkRelationship?.formControl.removeValidators(Validators.required);
        }
        checkRelationship?.formControl.updateValueAndValidity();
      }
    }

    if (data?.configs?.fixedKey === FixedPropertyKey.Payee) {
      const selectedPayee = data?.lstOption?.find((item: any) => item.value === data?.formControl.value);
      const checkRelationship = this.controls?.find(item => item.configs?.fixedKey === FixedPropertyKey.RelationShip)!;
      if (selectedPayee) {
        checkRelationship.lstOption.push({
          displayValue: selectedPayee.relationship,
          value: selectedPayee.relationshipLookupId,
        });
        checkRelationship?.formControl.setValue(selectedPayee.relationshipLookupId);
      }
    }
    this.currentRelationshipValue = value?.extraData?.relationshipType;
    if (this.currentRelationshipValue === RelationshipOptions.Spouse) {
      this.isSelectedSpouse = true;
    }
    const customError = this.validateRelationship(value?.extraData?.relationshipType);
    if (customError) data.formControl.setErrors({ customError });
    else data.formControl.clearValidators();
  }

  validateRelationship(relationshipType: number) {
    if (relationshipType != null) {
      const isBeneficiary = this.propertyReferences?.find((i: any) => i.entityComponentId === ENTITY_BENEFICIARIES_LIST_GUID);
      const isDependent = this.propertyReferences?.find((i: any) => i.entityComponentId === ENTITY_DEPENDENTS_LIST_GUID);
      let isPerson =  this.entityReal === ENTITY_PERSON_GUID;
      let isOrganization = this.entityReal === ENTITY_ORGANIZATION_GUID;
      if (isBeneficiary && relationshipType === RelationshipOptions.ExSpouse) {
        return 'Beneficiary cannot be an Ex-Spouse';
      }
      if (isBeneficiary || isDependent) {
        if (
          [
            RelationshipOptions.Spouse,
            RelationshipOptions.Child,
            RelationshipOptions.ExSpouse,
            RelationshipOptions.Parent,
            RelationshipOptions.Sibling,
            RelationshipOptions.DomesticPartner,
          ].includes(relationshipType) &&
          isOrganization
        ) {
          return `${isBeneficiary?.label || isDependent?.label} must be a Person`;
        } else if (RelationshipOptions.Organization === relationshipType && isPerson) {
          return `${isBeneficiary?.label || isDependent?.label} must be an organization`;
        }
      }
      const listPerson = Object.values(this.selectedRecordId);
      if (RelationshipOptions.Spouse === relationshipType && this.spousePerson && this.spousePerson.personId) {
        const isExactComponent = this.propertyReferences?.filter(x => this.spousePerson.componentIds.some((c: string) => c === x.entityComponentId));
        if (isExactComponent && (isExactComponent.length > 0 || (isExactComponent.length === 0 && listPerson[0] && this.spousePerson.personId !== listPerson[0]))) {
          return 'Only one spouse is allowed. Please change the previous spouse to ex-spouse first';
        }
      }
    }
    return null;
  }

  getPayeeProperties() {
    const checkPayee = this.controls?.find(item => item.configs?.fixedKey === FixedPropertyKey.Payee);
    if (checkPayee) {
      checkPayee.lstOption = [];
      checkPayee?.formControl.setValue('');
    }
    this.memberStore
      .pipe(
        select(getPayeePropertiesSelector),
        filter((state) => !!state),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((state: any) => {
        if (!state?.isLoading && state?.success && state?.payload) {
          if (checkPayee) {
            checkPayee.lstOption = state?.payload?.payeeOptions.map((item: PayeeOption) => {
              return {
                ...item,
                isHide: item.isHide,
                displayValue: item.name,
                value: item.id,
              };
            });
            const checkRelationship = this.controls?.find(item => item.configs?.fixedKey === FixedPropertyKey.RelationShip);
            const selectedPayee = checkPayee?.lstOption?.find((item: any) => item.value === checkPayee?.formControl.value);
            if (selectedPayee && checkRelationship) {
              checkRelationship.lstOption = [];
              checkRelationship?.formControl.setValue('');
              if (!state?.payload.courtOrderType) {
                checkRelationship.lstOption.push({
                  displayValue: selectedPayee.relationship,
                  value: selectedPayee.relationshipLookupId,
                });
                checkRelationship?.formControl.setValue(selectedPayee.relationshipLookupId);
              }
            }
          }
        }
        this.memberStore.dispatch(clearPayeePropertiesStateAction());
      });
  }

  getListUnique() {
    return this.controls
      .filter((el) => el.configs?.unique === 'true')
      .map((item) => {
        return { name: item.name, value: item.formControl.value };
      });
  }

  getUniqueOtherReference(property: InitiationProperty) {
    return this.createUniqueOtherReference(property);
  }

  onRecordChange({ id, value }: { id: string; value: string }) {
    if (!value) {
      this.isRecordExisted = false; // value == "" due to clear search result (not select any record)
    }
    this.selectedRecordId[id] = value;
    this.isAddNew = false;
    this.updateValidation();
  }

  getEntityReal(entityReal: string) {
    this.entityReal = entityReal;
    this.updateValidation();
  }

  onAddNewEntityReference(addNew: boolean) {
    this.isAddNew = addNew;
    this.updateValidation();
  }

  updateValidation() {
    if (this.currentRelationshipValue != null) {
      const customError = this.validateRelationship(this.currentRelationshipValue);
      const checkRelationship = this.controls.find(item => item.data?.options[0]?.relationshipType !== null);
      checkRelationship?.formControl.markAsTouched();
      if (customError)
        checkRelationship?.formControl.setErrors({ customError });
      else {
        checkRelationship?.formControl.clearValidators();
        checkRelationship?.formControl.updateValueAndValidity();
      }
    }
  }

  private _validateReportedDeceased(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!this.listMemberParticipants.includes(this.entityId) || control.value) {
        return of(null);
      }
      const formValue =this.formGroup.getRawValue();
      const isIncludeDateOfDeath = Object.keys(formValue).includes(PERSON_BASE_PROPERTIES.DateOfDeath.toLowerCase());
      const customError = {customError: 'Date of Death must be blank before Reported Deceased can be unchecked.'};
      if (isIncludeDateOfDeath) {
        if (!!formValue[PERSON_BASE_PROPERTIES.DateOfDeath.toLowerCase()]) {
          return of(customError);
        }
        return of(null);
      }
      return timer(100).pipe(
        switchMap(
          (): Observable<ValidationErrors | null> =>
            this.entityDataService.validateDateOfDeath([this.targetId]).pipe(
              map((res: ValidateReportedDeceasedResponse) => {
                if (res.infos.find(item => item.id === this.targetId)?.dateOfDeath) {
                  return customError;
                }
                return null;
              }),
              finalize(() => {
                this.formGroup.updateValueAndValidity();
              }),
            ),
        ),
      );
    };
  }

  private _validatePaymentInProgress(data: any, callback: (request: any) => void): void {
    if (!this.dateOfDeathControl?.value && !this.reportedDeceasedControl?.value) {
      callback(data);
      return;
    }
    this.entityDataService
      .checkPaymentInProgress([this.targetId])
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((isWarning) => {
        if (!isWarning) {
          callback(data);
          return;
        }
        const dialog = this.dialog.open(ConfirmPopupComponent, {
          panelClass: 'confirm-popup',
          data: {
            title: 'Warning',
            text: 'A payment is in progress for this payee. Please go to Processing module to make changes if needed.',
            type: ConfirmType.Warning,
            hideConfirmButton: true,
            cancelButtonTitle: BUTTON_LABEL_CLOSE,
          },
        });
        dialog.afterClosed().subscribe(() => {
          callback(data);
        })
      });
  }

  _checkIfRecordExistedSelector() {
    this.entityDataStore.select(checkIfRecordExistedSelector).pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe({
      next: (state) => {
        if (state && !state.isLoading && state.success) {
          this.isRecordExisted = state?.payload?.isExist ?? false;
        }
      }
    })
  }
}
